import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Slider,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Filter, X, Search, ChevronDown } from 'lucide-react';

export interface FilterOptions {
  skills: string[];
  locations: string[];
  experienceLevels: string[];
  status: string[];
  matchRange: [number, number];
  availability: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  onClearFilters: () => void;
  availableSkills: string[];
  availableLocations: string[];
}

const EXPERIENCE_LEVELS = [
  { value: '0-2', label: '0-2 years' },
  { value: '2-5', label: '2-5 years' },
  { value: '5-8', label: '5-8 years' },
  { value: '8+', label: '8+ years' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open to roles', color: 'success' as const },
  { value: 'active', label: 'Actively looking', color: 'primary' as const },
  { value: 'unavailable', label: 'Not available', color: 'default' as const },
];

const AVAILABILITY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: '2-weeks', label: 'Within 2 weeks' },
  { value: '1-month', label: 'Within 1 month' },
  { value: '3-months', label: 'Within 3 months' },
];

const SORT_OPTIONS = [
  { value: 'matchPercentage', label: 'Match Score' },
  { value: 'name', label: 'Name' },
  { value: 'experience', label: 'Experience' },
  { value: 'status', label: 'Status' },
  { value: 'location', label: 'Location' },
];

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  availableSkills,
  availableLocations,
}) => {
  const [localFilters, setLocalFilters] = useState<FilterOptions>(filters);
  const [skillSearch, setSkillSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterUpdate = (key: keyof FilterOptions, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClearAll = () => {
    setLocalFilters({
      skills: [],
      locations: [],
      experienceLevels: [],
      status: [],
      matchRange: [0, 100],
      availability: [],
      sortBy: 'matchPercentage',
      sortOrder: 'desc',
    });
  };

  const handleSkillToggle = (skill: string) => {
    const newSkills = localFilters.skills.includes(skill)
      ? localFilters.skills.filter(s => s !== skill)
      : [...localFilters.skills, skill];
    handleFilterUpdate('skills', newSkills);
  };

  const handleLocationToggle = (location: string) => {
    const newLocations = localFilters.locations.includes(location)
      ? localFilters.locations.filter(l => l !== location)
      : [...localFilters.locations, location];
    handleFilterUpdate('locations', newLocations);
  };

  const handleStatusToggle = (status: string) => {
    const newStatus = localFilters.status.includes(status)
      ? localFilters.status.filter(s => s !== status)
      : [...localFilters.status, status];
    handleFilterUpdate('status', newStatus);
  };

  const filteredSkills = availableSkills.filter(skill =>
    skill.toLowerCase().includes(skillSearch.toLowerCase()),
  );

  const filteredLocations = availableLocations.filter(location =>
    location.toLowerCase().includes(locationSearch.toLowerCase()),
  );

  const getActiveFiltersCount = () => {
    return localFilters.skills.length +
           localFilters.locations.length +
           localFilters.experienceLevels.length +
           localFilters.status.length +
           localFilters.availability.length +
           (localFilters.matchRange[0] > 0 || localFilters.matchRange[1] < 100 ? 1 : 0);
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 380 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Filter size={18} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'common.white', lineHeight: 1.2 }}>
              Advanced Filters
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.light', fontWeight: 600 }}>
              {getActiveFiltersCount()} active filters
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'common.white', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Accordion Sections */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Skills */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Skills</Typography>
              {localFilters.skills.length > 0 && (
                <Chip label={localFilters.skills.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700 }} />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                placeholder="Search skills..."
                value={skillSearch}
                onChange={(e) => setSkillSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={14} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 } }}
              />
              <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                <Stack spacing={0.5}>
                  {filteredSkills.map((skill) => (
                    <FormControlLabel
                      key={skill}
                      control={
                        <Checkbox
                          size="small"
                          checked={localFilters.skills.includes(skill)}
                          onChange={() => handleSkillToggle(skill)}
                        />
                      }
                      label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{skill}</Typography>}
                      sx={{ m: 0 }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Location */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Location</Typography>
              {localFilters.locations.length > 0 && (
                <Chip label={localFilters.locations.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700 }} />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                placeholder="Search locations..."
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={14} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 } }}
              />
              <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                <Stack spacing={0.5}>
                  {filteredLocations.map((location) => (
                    <FormControlLabel
                      key={location}
                      control={
                        <Checkbox
                          size="small"
                          checked={localFilters.locations.includes(location)}
                          onChange={() => handleLocationToggle(location)}
                        />
                      }
                      label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{location}</Typography>}
                      sx={{ m: 0 }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Experience Level */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Experience Level</Typography>
              {localFilters.experienceLevels.length > 0 && (
                <Chip label={localFilters.experienceLevels.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700 }} />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={0.5}>
              {EXPERIENCE_LEVELS.map((exp) => (
                <FormControlLabel
                  key={exp.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={localFilters.experienceLevels.includes(exp.value)}
                      onChange={() => {
                        const newLevels = localFilters.experienceLevels.includes(exp.value)
                          ? localFilters.experienceLevels.filter(l => l !== exp.value)
                          : [...localFilters.experienceLevels, exp.value];
                        handleFilterUpdate('experienceLevels', newLevels);
                      }}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{exp.label}</Typography>}
                  sx={{ m: 0 }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Status */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Status</Typography>
              {localFilters.status.length > 0 && (
                <Chip label={localFilters.status.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700 }} />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={0.5}>
              {STATUS_OPTIONS.map((status) => (
                <FormControlLabel
                  key={status.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={localFilters.status.includes(status.value)}
                      onChange={() => handleStatusToggle(status.value)}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{status.label}</Typography>}
                  sx={{ m: 0 }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Match Score */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Match Score</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 3 }}>
            <Slider
              value={localFilters.matchRange[1]}
              min={0}
              max={100}
              onChange={(_, val) => handleFilterUpdate('matchRange', [localFilters.matchRange[0], val as number])}
              valueLabelDisplay="auto"
            />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{localFilters.matchRange[0]}%</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Up to {localFilters.matchRange[1]}%
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Availability */}
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Availability</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={0.5}>
              {AVAILABILITY_OPTIONS.map((avail) => (
                <FormControlLabel
                  key={avail.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={localFilters.availability.includes(avail.value)}
                      onChange={() => {
                        const newAvail = localFilters.availability.includes(avail.value)
                          ? localFilters.availability.filter(a => a !== avail.value)
                          : [...localFilters.availability, avail.value];
                        handleFilterUpdate('availability', newAvail);
                      }}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{avail.label}</Typography>}
                  sx={{ m: 0 }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Sort By */}
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <AccordionSummary expandIcon={<ChevronDown size={16} />}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>Sort Options</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              <Select
                size="small"
                value={localFilters.sortBy}
                onChange={(e) => handleFilterUpdate('sortBy', e.target.value)}
                sx={{ fontSize: '0.75rem' }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <RadioGroup
                row
                value={localFilters.sortOrder}
                onChange={(e) => handleFilterUpdate('sortOrder', e.target.value as 'asc' | 'desc')}
              >
                <FormControlLabel value="desc" control={<Radio size="small" />} label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>High to Low</Typography>} />
                <FormControlLabel value="asc" control={<Radio size="small" />} label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Low to High</Typography>} />
              </RadioGroup>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50', display: 'flex', gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={handleClearAll}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
        >
          Clear All
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleApplyFilters}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem' }}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
};
